import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Country, State, City } from "country-state-city";

import {
  ChevronRight,
  Building2,
  UserRound,
  BriefcaseBusiness,
  WalletCards,
  UsersRound,
  ShieldCheck,
  UploadCloud,
  CreditCard,
  CheckCircle2,
  Send,
  ArrowRight,
  Save,
  ArrowLeft,
  FileText,
  BadgeCheck,
  ClipboardCheck,
  AlertCircle,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import ApplicationHeader from "./ApplicationHeader";

gsap.registerPlugin(ScrollTrigger);

const HERO_IMAGE = "/assets/images/bg2.jpeg";

const initialFormData = {
  applicationType: "",
  applicantCategory: "",
  incorporationType: "",
  preferredFreeZone: "",

  proposedCompanyName: "",
  existingCompanyName: "",
  incorporationCountry: "",
  incorporationState: "",
  incorporationCity: "",
  dateOfIncorporation: "",
  companyRegistrationNumber: "",
  taxIdentificationNumber: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  headOfficeCountry: "",
  headOfficeState: "",
  headOfficeCity: "",
  headOfficeAddress: "",
  nigerianOfficeCountry: "NG",
  nigerianOfficeState: "",
  nigerianOfficeCity: "",
  nigerianOfficeAddress: "",
  postalAddress: "",

  contactFullName: "",
  contactDesignation: "",
  contactDepartment: "",
  contactPhone: "",
  contactEmail: "",
  contactNationality: "",
  meansOfIdentification: "",
  identificationNumber: "",

  currentBusinessNature: "",
  proposedBusinessActivity: "",
  businessSector: "",
  proposedOperations: "",
  countriesOfOperation: "",
  commencementDate: "",
  expectedEmployees: "",
  expectedNigerianEmployees: "",
  expectedExpatriateEmployees: "",
  localContentPlan: "",

  investmentValue: "",
  currency: "",
  fundingSource: "",
  fundingInstitution: "",
  projectedTurnover: "",
  projectedImportValue: "",
  projectedExportValue: "",
  authorisedShareCapital: "",
  paidUpShareCapital: "",
  ownershipDetails: "",

  directorDetails: "",
  keyPersonnelDetails: "",

  leasedSpace: "",
  zoneManagementCompany: "",
  facilityCountry: "NG",
  facilityState: "",
  facilityCity: "",
  facilityLocation: "",
  plotNumber: "",
  facilitySize: "",
  facilityType: "",
  utilityRequirements: "",

  requiresEia: "",
  hazardousMaterials: "",
  emissionsDischarge: "",
  complianceOfficerName: "",
  complianceOfficerPhone: "",
  complianceOfficerEmail: "",

  paymentAmount: "",
  paymentReference: "",
  paymentDate: "",
  paymentChannel: "",

  declarationAccuracy: false,
  declarationAuthenticity: false,
  declarationVerification: false,
  declarationCompliance: false,
  declarationDataConsent: false,
  signatoryName: "",
  signatoryDesignation: "",
  signatureDate: "",
};

const steps = [
  {
    title: "Application Type",
    note: "Select the type of licence request.",
    icon: ClipboardCheck,
  },
  {
    title: "Company Details",
    note: "Provide company registration and contact information.",
    icon: Building2,
  },
  {
    title: "Authorised Contact",
    note: "Provide the authorised representative details.",
    icon: UserRound,
  },
  {
    title: "Business Activity",
    note: "Describe the proposed Free Zone activity.",
    icon: BriefcaseBusiness,
  },
  {
    title: "Investment & Ownership",
    note: "Provide funding, investment and ownership details.",
    icon: WalletCards,
  },
  {
    title: "Directors & Facility",
    note: "Provide directors and facility details.",
    icon: UsersRound,
  },
  {
    title: "HSE & Compliance",
    note: "Provide compliance and safety information.",
    icon: ShieldCheck,
  },
  {
    title: "Document Uploads",
    note: "Upload required supporting documents.",
    icon: UploadCloud,
  },
  {
    title: "Payment",
    note: "Provide payment confirmation details.",
    icon: CreditCard,
  },
  {
    title: "Declaration",
    note: "Confirm and submit the application.",
    icon: BadgeCheck,
  },
];

const requiredFieldsByStep = {
  0: [
    "applicationType",
    "applicantCategory",
    "incorporationType",
    "preferredFreeZone",
  ],
  1: [
    "proposedCompanyName",
    "incorporationCountry",
    "incorporationState",
    "incorporationCity",
    "companyRegistrationNumber",
    "companyEmail",
    "companyPhone",
    "headOfficeCountry",
    "headOfficeState",
    "headOfficeCity",
    "headOfficeAddress",
  ],
  2: ["contactFullName", "contactDesignation", "contactPhone", "contactEmail"],
  3: ["proposedBusinessActivity", "businessSector", "proposedOperations"],
  4: ["investmentValue", "currency", "fundingSource", "ownershipDetails"],
  5: [
    "directorDetails",
    "leasedSpace",
    "facilityCountry",
    "facilityState",
    "facilityCity",
    "facilityLocation",
  ],
  6: [],
  7: [],
  8: ["paymentReference"],
  9: [
    "declarationAccuracy",
    "declarationAuthenticity",
    "declarationVerification",
    "declarationCompliance",
    "declarationDataConsent",
    "signatoryName",
    "signatoryDesignation",
  ],
};

const applicationTypes = [
  "New licence",
  "Licence renewal",
  "Licence amendment",
  "Additional business activity approval",
  "Change of company details",
];

const applicantCategories = [
  "Free Zone enterprise",
  "Free Zone developer/promoter",
  "Existing OGFZA client",
  "New investor",
];

const freeZones = [
  "Onne Oil and Gas Free Zone",
  "Warri Oil and Gas Free Zone",
  "Liberty Oil and Gas Free Zone",
  "Brass Oil and Gas Free Zone",
  "Eko Support Free Zone",
  "Dangote Industrial Free Zone",
  "Other approved Free Zone",
];

const businessSectors = [
  "Oil and gas services",
  "Exploration support",
  "Drilling services",
  "Fabrication",
  "Manufacturing",
  "Marine services",
  "Logistics",
  "Warehousing",
  "Oilfield equipment supply",
  "Engineering services",
  "Gas processing",
  "Petrochemicals",
  "Power / energy services",
  "Other",
];

const uploadFields = [
  "Completed application form",
  "OGFZA Forms A1, A2 and A3",
  "Certificate of incorporation",
  "Memorandum and Articles of Association",
  "Beneficial Owners Disclosure Form",
  "Company profile / brochure",
  "Financial profile / source of funding",
  "Feasibility study / business plan",
  "EIA report, where applicable",
  "Key personnel profile",
  "Confirmation of facility / space leased",
  "Payment evidence",
];

const RegistryPortal = () => {
  const pageRef = useRef(null);
  const countries = useMemo(() => Country.getAllCountries(), []);

  const [activeStep, setActiveStep] = useState(0);
  const [highestAllowedStep, setHighestAllowedStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [savedMessage, setSavedMessage] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const savedDraft = localStorage.getItem("ogfzaRegistryPortalDraft");

    if (savedDraft) {
      setFormData({ ...initialFormData, ...JSON.parse(savedDraft) });
    }
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".portal-fade", {
        opacity: 0,
        y: 22,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.08,
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (field, file) => {
    setUploadedFiles((prev) => ({ ...prev, [field]: file }));
  };

  const validateStep = (stepIndex = activeStep) => {
    const requiredFields = requiredFieldsByStep[stepIndex] || [];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required.";
      }
    });

    if (
      formData.companyEmail &&
      !/^\S+@\S+\.\S+$/.test(formData.companyEmail)
    ) {
      newErrors.companyEmail = "Enter a valid email address.";
    }

    if (
      formData.contactEmail &&
      !/^\S+@\S+\.\S+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = "Enter a valid email address.";
    }

    if (
      formData.complianceOfficerEmail &&
      !/^\S+@\S+\.\S+$/.test(formData.complianceOfficerEmail)
    ) {
      newErrors.complianceOfficerEmail = "Enter a valid email address.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const saveDraft = () => {
    localStorage.setItem("ogfzaRegistryPortalDraft", JSON.stringify(formData));
    setSavedMessage("Draft saved successfully.");

    setTimeout(() => {
      setSavedMessage("");
    }, 3000);
  };

  const nextStep = () => {
    if (!validateStep(activeStep)) return;

    setHighestAllowedStep((prev) => Math.max(prev, activeStep + 1));
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const previousStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    for (let i = 0; i < steps.length; i += 1) {
      const requiredFields = requiredFieldsByStep[i] || [];
      const invalid = requiredFields.some((field) => !formData[field]);

      if (invalid) {
        setActiveStep(i);
        validateStep(i);
        return;
      }
    }

    saveDraft();
    alert("Application submitted successfully.");
  };

  const ErrorText = ({ text }) => (
    <span className="mt-2 flex items-center gap-1 text-xs font-medium text-red-600">
      <AlertCircle size={13} />
      {text}
    </span>
  );

  const Input = ({ label, field, type = "text", placeholder, required }) => (
    <label className="block">
      <span className="block text-[13px] font-medium text-slate-700 mb-2">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>

      <input
        type={type}
        value={formData[field]}
        onChange={(e) => updateField(field, e.target.value)}
        placeholder={placeholder}
        className={`w-full h-12 rounded-xl border bg-white px-4 text-[14px] text-slate-800 outline-none transition-all duration-200 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10 ${
          errors[field] ? "border-red-400" : "border-slate-300"
        }`}
      />

      {errors[field] && <ErrorText text={errors[field]} />}
    </label>
  );

  const Select = ({ label, field, options, required }) => (
    <label className="block">
      <span className="block text-[13px] font-medium text-slate-700 mb-2">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>

      <select
        value={formData[field]}
        onChange={(e) => updateField(field, e.target.value)}
        className={`w-full h-12 rounded-xl border bg-white px-4 text-[14px] text-slate-800 outline-none transition-all duration-200 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10 ${
          errors[field] ? "border-red-400" : "border-slate-300"
        }`}
      >
        <option value="">Select option</option>

        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      {errors[field] && <ErrorText text={errors[field]} />}
    </label>
  );

  const Textarea = ({ label, field, placeholder, required }) => (
    <label className="block">
      <span className="block text-[13px] font-medium text-slate-700 mb-2">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>

      <textarea
        rows="5"
        value={formData[field]}
        onChange={(e) => updateField(field, e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-xl border bg-white px-4 py-3 text-[14px] text-slate-800 outline-none transition-all duration-200 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10 resize-none ${
          errors[field] ? "border-red-400" : "border-slate-300"
        }`}
      />

      {errors[field] && <ErrorText text={errors[field]} />}
    </label>
  );

  const SelectCountry = ({ label, field, required }) => (
    <label className="block">
      <span className="block text-[13px] font-medium text-slate-700 mb-2">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>

      <select
        value={formData[field]}
        onChange={(e) => {
          updateField(field, e.target.value);

          if (field === "incorporationCountry") {
            updateField("incorporationState", "");
            updateField("incorporationCity", "");
          }

          if (field === "headOfficeCountry") {
            updateField("headOfficeState", "");
            updateField("headOfficeCity", "");
          }

          if (field === "nigerianOfficeCountry") {
            updateField("nigerianOfficeState", "");
            updateField("nigerianOfficeCity", "");
          }

          if (field === "facilityCountry") {
            updateField("facilityState", "");
            updateField("facilityCity", "");
          }
        }}
        className={`w-full h-12 rounded-xl border bg-white px-4 text-[14px] text-slate-800 outline-none transition-all duration-200 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10 ${
          errors[field] ? "border-red-400" : "border-slate-300"
        }`}
      >
        <option value="">Select country</option>

        {countries.map((country) => (
          <option key={country.isoCode} value={country.isoCode}>
            {country.name}
          </option>
        ))}
      </select>

      {errors[field] && <ErrorText text={errors[field]} />}
    </label>
  );

  const CountryStateCity = ({
    title,
    countryField,
    stateField,
    cityField,
    required,
  }) => {
    const selectedCountry = formData[countryField];
    const selectedState = formData[stateField];

    const states = selectedCountry
      ? State.getStatesOfCountry(selectedCountry)
      : [];

    const cities =
      selectedCountry && selectedState
        ? City.getCitiesOfState(selectedCountry, selectedState)
        : [];

    return (
      <>
        <SelectCountry
          label={`${title} country`}
          field={countryField}
          required={required}
        />

        <label className="block">
          <span className="block text-[13px] font-medium text-slate-700 mb-2">
            {title} state/province
            {required && <span className="text-red-600"> *</span>}
          </span>

          <select
            value={selectedState}
            onChange={(e) => {
              updateField(stateField, e.target.value);
              updateField(cityField, "");
            }}
            disabled={!selectedCountry}
            className={`w-full h-12 rounded-xl border bg-white px-4 text-[14px] text-slate-800 outline-none transition-all duration-200 disabled:bg-slate-100 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10 ${
              errors[stateField] ? "border-red-400" : "border-slate-300"
            }`}
          >
            <option value="">Select state/province</option>

            {states.map((state) => (
              <option key={state.isoCode} value={state.isoCode}>
                {state.name}
              </option>
            ))}
          </select>

          {errors[stateField] && <ErrorText text={errors[stateField]} />}
        </label>

        <label className="block">
          <span className="block text-[13px] font-medium text-slate-700 mb-2">
            {title} city
            {required && <span className="text-red-600"> *</span>}
          </span>

          <select
            value={formData[cityField]}
            onChange={(e) => updateField(cityField, e.target.value)}
            disabled={!selectedState}
            className={`w-full h-12 rounded-xl border bg-white px-4 text-[14px] text-slate-800 outline-none transition-all duration-200 disabled:bg-slate-100 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10 ${
              errors[cityField] ? "border-red-400" : "border-slate-300"
            }`}
          >
            <option value="">Select city</option>

            {cities.map((city) => (
              <option
                key={`${city.name}-${city.latitude}-${city.longitude}`}
                value={city.name}
              >
                {city.name}
              </option>
            ))}
          </select>

          {errors[cityField] && <ErrorText text={errors[cityField]} />}
        </label>
      </>
    );
  };

  const UploadField = ({ label }) => (
    <label className="block rounded-xl border border-dashed border-slate-300 bg-white p-5 hover:border-[#201182] hover:bg-slate-50 transition-all duration-200 cursor-pointer">
      <div className="w-10 h-10 rounded-lg bg-[#201182]/10 text-[#201182] flex items-center justify-center mb-4">
        <UploadCloud size={20} />
      </div>

      <span className="block text-[14px] font-medium text-slate-900">
        {label}
      </span>

      <span className="block text-[13px] text-slate-500 mt-2">
        Click to upload document
      </span>

      <input
        type="file"
        className="hidden"
        onChange={(e) => handleFileChange(label, e.target.files?.[0])}
      />

      {uploadedFiles[label] && (
        <span className="mt-4 flex items-center gap-2 text-[13px] font-medium text-[#026b07]">
          <CheckCircle2 size={15} />
          {uploadedFiles[label].name}
        </span>
      )}
    </label>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Select
              label="Type of application"
              field="applicationType"
              options={applicationTypes}
              required
            />

            <Select
              label="Applicant category"
              field="applicantCategory"
              options={applicantCategories}
              required
            />

            <Select
              label="Incorporation type"
              field="incorporationType"
              options={[
                "Offshore / overseas incorporation",
                "Free Zone incorporation",
              ]}
              required
            />

            <Select
              label="Preferred Free Zone location"
              field="preferredFreeZone"
              options={freeZones}
              required
            />
          </div>
        );

      case 1:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Input
              label="Proposed company name"
              field="proposedCompanyName"
              placeholder="Enter proposed company name"
              required
            />

            <Input
              label="Existing company name, if already registered"
              field="existingCompanyName"
              placeholder="Enter existing company name"
            />

            <CountryStateCity
              title="Incorporation"
              countryField="incorporationCountry"
              stateField="incorporationState"
              cityField="incorporationCity"
              required
            />

            <Input
              label="Date of incorporation"
              field="dateOfIncorporation"
              type="date"
            />

            <Input
              label="Company registration number"
              field="companyRegistrationNumber"
              placeholder="Enter registration number"
              required
            />

            <Input
              label="Tax identification number"
              field="taxIdentificationNumber"
              placeholder="Enter tax identification number"
            />

            <Input
              label="Company email address"
              field="companyEmail"
              type="email"
              placeholder="company@example.com"
              required
            />

            <Input
              label="Company phone number"
              field="companyPhone"
              placeholder="+234..."
              required
            />

            <Input
              label="Company website"
              field="companyWebsite"
              placeholder="https://"
            />

            <Input
              label="Postal address"
              field="postalAddress"
              placeholder="Enter postal address"
            />

            <div className="md:col-span-2 grid md:grid-cols-3 gap-5">
              <CountryStateCity
                title="Head office"
                countryField="headOfficeCountry"
                stateField="headOfficeState"
                cityField="headOfficeCity"
                required
              />
            </div>

            <div className="md:col-span-2">
              <Textarea
                label="Head office street address"
                field="headOfficeAddress"
                placeholder="Enter full street address"
                required
              />
            </div>

            <div className="md:col-span-2 grid md:grid-cols-3 gap-5">
              <CountryStateCity
                title="Nigerian office"
                countryField="nigerianOfficeCountry"
                stateField="nigerianOfficeState"
                cityField="nigerianOfficeCity"
              />
            </div>

            <div className="md:col-span-2">
              <Textarea
                label="Nigerian office street address, if any"
                field="nigerianOfficeAddress"
                placeholder="Enter Nigerian office street address"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Input
              label="Full name"
              field="contactFullName"
              placeholder="Enter full name"
              required
            />

            <Input
              label="Designation"
              field="contactDesignation"
              placeholder="Enter designation"
              required
            />

            <Input
              label="Department"
              field="contactDepartment"
              placeholder="Enter department"
            />

            <Input
              label="Phone number"
              field="contactPhone"
              placeholder="+234..."
              required
            />

            <Input
              label="Email address"
              field="contactEmail"
              type="email"
              placeholder="name@example.com"
              required
            />

            <SelectCountry label="Nationality" field="contactNationality" />

            <Select
              label="Means of identification"
              field="meansOfIdentification"
              options={[
                "International passport",
                "National ID",
                "Driver’s licence",
                "Voter’s card",
              ]}
            />

            <Input
              label="Identification number"
              field="identificationNumber"
              placeholder="Enter identification number"
            />
          </div>
        );

      case 3:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Input
              label="Nature of current business"
              field="currentBusinessNature"
              placeholder="Enter current business"
            />

            <Input
              label="Proposed Free Zone business activity"
              field="proposedBusinessActivity"
              placeholder="Enter proposed activity"
              required
            />

            <Select
              label="Business sector"
              field="businessSector"
              options={businessSectors}
              required
            />

            <Input
              label="Expected commencement date"
              field="commencementDate"
              type="date"
            />

            <Input
              label="Countries of operation in West Africa"
              field="countriesOfOperation"
              placeholder="Enter countries"
            />

            <Input
              label="Expected number of employees"
              field="expectedEmployees"
              type="number"
              placeholder="0"
            />

            <Input
              label="Expected Nigerian employees"
              field="expectedNigerianEmployees"
              type="number"
              placeholder="0"
            />

            <Input
              label="Expected expatriate employees"
              field="expectedExpatriateEmployees"
              type="number"
              placeholder="0"
            />

            <div className="md:col-span-2">
              <Textarea
                label="Brief description of proposed operations"
                field="proposedOperations"
                placeholder="Describe the business operation"
                required
              />
            </div>

            <div className="md:col-span-2">
              <Textarea
                label="Local content plan summary"
                field="localContentPlan"
                placeholder="Summarise your local content plan"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Input
              label="Estimated value of investment"
              field="investmentValue"
              type="number"
              placeholder="Enter amount"
              required
            />

            <Select
              label="Currency"
              field="currency"
              options={["US Dollar", "Naira", "Euro", "Pounds Sterling", "Other"]}
              required
            />

            <Select
              label="Source of funding"
              field="fundingSource"
              options={[
                "Equity",
                "Bank loan",
                "Parent company funding",
                "Foreign direct investment",
                "Joint venture",
                "Other",
              ]}
              required
            />

            <Input
              label="Funding institution, if applicable"
              field="fundingInstitution"
              placeholder="Enter funding institution"
            />

            <Input
              label="Projected annual turnover"
              field="projectedTurnover"
              type="number"
              placeholder="Enter amount"
            />

            <Input
              label="Projected import value"
              field="projectedImportValue"
              type="number"
              placeholder="Enter amount"
            />

            <Input
              label="Projected export value"
              field="projectedExportValue"
              type="number"
              placeholder="Enter amount"
            />

            <Input
              label="Authorised share capital"
              field="authorisedShareCapital"
              placeholder="Enter share capital"
            />

            <Input
              label="Paid-up share capital"
              field="paidUpShareCapital"
              placeholder="Enter paid-up capital"
            />

            <div className="md:col-span-2">
              <Textarea
                label="Shareholders and beneficial ownership details"
                field="ownershipDetails"
                placeholder="Add shareholder names, nationality, address, percentage shareholding and beneficial ownership details."
                required
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Textarea
              label="Director details"
              field="directorDetails"
              placeholder="Add director name, position, nationality, address, phone, email, occupation and ID number."
              required
            />

            <Textarea
              label="Key personnel details"
              field="keyPersonnelDetails"
              placeholder="Add key personnel name, position, qualification, experience and nationality."
            />

            <Select
              label="Has the company leased space in the Free Zone?"
              field="leasedSpace"
              options={["Yes", "No", "In progress"]}
              required
            />

            <Input
              label="Free Zone management company"
              field="zoneManagementCompany"
              placeholder="Enter management company"
            />

            <div className="md:col-span-2 grid md:grid-cols-3 gap-5">
              <CountryStateCity
                title="Facility"
                countryField="facilityCountry"
                stateField="facilityState"
                cityField="facilityCity"
                required
              />
            </div>

            <Input
              label="Facility / space street location"
              field="facilityLocation"
              placeholder="Enter facility location"
              required
            />

            <Input
              label="Plot / unit number"
              field="plotNumber"
              placeholder="Enter plot or unit number"
            />

            <Input
              label="Size of facility required"
              field="facilitySize"
              placeholder="Enter size"
            />

            <Select
              label="Type of facility"
              field="facilityType"
              options={[
                "Office",
                "Warehouse",
                "Yard",
                "Fabrication space",
                "Manufacturing plant",
                "Jetty / marine facility",
                "Tank farm",
                "Processing plant",
                "Other",
              ]}
            />

            <div className="md:col-span-2">
              <Textarea
                label="Utility requirements"
                field="utilityRequirements"
                placeholder="Power, water, gas, internet, port access, road access, waste management, security, etc."
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Select
              label="Does the project require Environmental Impact Assessment?"
              field="requiresEia"
              options={["Yes", "No", "Not sure"]}
            />

            <Input
              label="Compliance officer name"
              field="complianceOfficerName"
              placeholder="Enter name"
            />

            <Input
              label="Compliance officer phone number"
              field="complianceOfficerPhone"
              placeholder="+234..."
            />

            <Input
              label="Compliance officer email"
              field="complianceOfficerEmail"
              type="email"
              placeholder="officer@example.com"
            />

            <Textarea
              label="Expected hazardous materials, if any"
              field="hazardousMaterials"
              placeholder="List hazardous materials, if applicable"
            />

            <Textarea
              label="Expected emissions or discharge, if any"
              field="emissionsDischarge"
              placeholder="Describe emissions or discharge, if applicable"
            />
          </div>
        );

      case 7:
        return (
          <div>
            <div className="mb-6 rounded-xl bg-slate-50 border border-slate-200 p-5">
              <h4 className="font-semibold text-[#201182] mb-2">
                Supporting documents
              </h4>

              <p className="text-sm text-slate-600 leading-relaxed">
                Upload the documents required for the application review.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {uploadFields.map((item) => (
                <UploadField key={item} label={item} />
              ))}
            </div>
          </div>
        );

      case 8:
        return (
          <div className="grid md:grid-cols-2 gap-5">
            <Input
              label="Application fee amount"
              field="paymentAmount"
              placeholder="Enter amount"
            />

            <Input
              label="Payment reference number"
              field="paymentReference"
              placeholder="Enter payment reference"
              required
            />

            <Input label="Payment date" field="paymentDate" type="date" />

            <Select
              label="Payment channel"
              field="paymentChannel"
              options={["Bank transfer", "Online payment", "Remita", "Other"]}
            />

            <div className="md:col-span-2">
              <UploadField label="Upload payment evidence" />
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-8">
            <div className="rounded-xl bg-white border border-slate-200 p-6">
              <h4 className="text-lg font-semibold text-[#201182] mb-5">
                Declaration
              </h4>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  {
                    field: "declarationAccuracy",
                    text: "I confirm that the information provided is accurate.",
                  },
                  {
                    field: "declarationAuthenticity",
                    text: "I confirm that all uploaded documents are authentic.",
                  },
                  {
                    field: "declarationVerification",
                    text: "I consent to OGFZA verifying the submitted documents.",
                  },
                  {
                    field: "declarationCompliance",
                    text: "I agree to comply with OGFZA laws, regulations and operational guidelines.",
                  },
                  {
                    field: "declarationDataConsent",
                    text: "I consent to processing of submitted data for application review.",
                  },
                ].map((item) => (
                  <label
                    key={item.field}
                    className="block rounded-xl border border-slate-200 p-4 text-sm text-slate-700 leading-relaxed"
                  >
                    <span className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={formData[item.field]}
                        onChange={(e) =>
                          updateField(item.field, e.target.checked)
                        }
                        className="mt-1 w-4 h-4 accent-[#026b07]"
                      />

                      <span>{item.text}</span>
                    </span>

                    {errors[item.field] && (
                      <ErrorText text={errors[item.field]} />
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              <Input
                label="Name of authorised signatory"
                field="signatoryName"
                placeholder="Enter full name"
                required
              />

              <Input
                label="Designation"
                field="signatoryDesignation"
                placeholder="Enter designation"
                required
              />

              <Input label="Date" field="signatureDate" type="date" />
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-6">
              <h4 className="font-semibold text-lg mb-2 text-[#201182]">
                Final review
              </h4>

              <p className="text-sm text-slate-600 leading-relaxed">
                Confirm the application information before submission.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const ActiveIcon = steps[activeStep].icon;
  const progressPercent = Math.round(((activeStep + 1) / steps.length) * 100);
  const nextSection = steps[activeStep + 1];

  return (
    <main ref={pageRef} className="font-sans bg-slate-50 text-slate-800">
      <ApplicationHeader />

      <section className="relative bg-white border-b border-slate-200">
        <div className="relative min-h-[360px] overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="OGFZA Registry Portal"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#020617]/65" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#020617]/90 via-[#201182]/55 to-[#026b07]/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/75 via-transparent to-transparent" />

          <div className="relative max-w-5xl mx-auto px-5 py-16 md:py-20 text-white">
            <div className="portal-fade inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-medium text-[#f2af11] mb-5 backdrop-blur">
              <span className="w-2 h-2 rounded-full bg-[#f2af11]" />
              Digital Registry Portal
            </div>

            <h1 className="portal-fade text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
              Free Zone Licence Application
            </h1>

            <p className="portal-fade text-white/80 max-w-2xl mt-4 leading-relaxed text-[15px]">
              Complete the application section by section. Required sections
              must be completed before moving forward.
            </p>

            <div className="portal-fade flex items-center gap-2 mt-5 text-sm text-white/75 flex-wrap">
              <Link
                to="/"
                className="hover:text-[#f2af11] transition-colors"
              >
                Home
              </Link>

              <ChevronRight size={14} />

              <Link
                to="/portal"
                className="hover:text-[#f2af11] transition-colors"
              >
                Portal
              </Link>

              <ChevronRight size={14} />

              <span className="text-[#f2af11]">Registry Portal</span>
            </div>
          </div>
        </div>
      </section>

      <section id="application" className="max-w-5xl mx-auto px-5 py-10 md:py-14">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-white border-b border-slate-200">
            <div className="px-5 md:px-7 py-5">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#201182]/10 text-[#201182] flex items-center justify-center shrink-0">
                    <ActiveIcon size={21} />
                  </div>

                  <div>
                    <p className="text-xs font-medium text-[#026b07] uppercase tracking-[0.14em]">
                      Section {activeStep + 1} of {steps.length}
                    </p>

                    <h2 className="text-xl md:text-2xl font-semibold text-slate-950 mt-1">
                      {steps[activeStep].title}
                    </h2>

                    <p className="text-sm text-slate-500 mt-1">
                      {steps[activeStep].note}
                    </p>

                    {nextSection ? (
                      <p className="text-xs text-slate-500 mt-3">
                        Next:{" "}
                        <span className="font-medium text-[#201182]">
                          {nextSection.title}
                        </span>
                      </p>
                    ) : (
                      <p className="text-xs text-[#026b07] mt-3 font-medium">
                        Final section. Review and submit your application.
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={saveDraft}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-slate-700 text-sm font-medium hover:border-[#201182] hover:text-[#201182] transition-all duration-200"
                >
                  <Save size={16} />
                  Save Draft
                </button>
              </div>

              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                  <span>Application progress</span>
                  <span>{progressPercent}%</span>
                </div>

                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-[#201182] rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-5 md:p-7">
            {savedMessage && (
              <div className="mb-6 rounded-xl bg-[#f2af11]/10 border border-[#f2af11]/30 px-4 py-3 text-[#201182] text-sm font-medium">
                {savedMessage}
              </div>
            )}

            {renderStepContent()}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-10 pt-7 border-t border-slate-200">
              <button
                type="button"
                onClick={previousStep}
                disabled={activeStep === 0}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
              >
                <ArrowLeft size={16} />
                Previous
              </button>

              <div className="flex w-full sm:w-auto gap-3">
                <button
                  type="button"
                  onClick={saveDraft}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-slate-700 text-sm font-medium hover:border-[#201182] hover:text-[#201182] transition-all duration-200"
                >
                  <Save size={16} />
                  Save
                </button>

                {activeStep < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-xl bg-[#201182] px-6 py-3 text-white text-sm font-medium hover:bg-[#026b07] transition-all duration-200"
                  >
                    Next
                    <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-xl bg-[#026b07] px-6 py-3 text-white text-sm font-medium hover:bg-[#201182] transition-all duration-200"
                  >
                    Submit
                    <Send size={16} />
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            {
              icon: Save,
              title: "Save progress",
              text: "Applicants can save their application and continue later.",
            },
            {
              icon: FileText,
              title: "Document upload",
              text: "Supporting documents are grouped clearly for review.",
            },
            {
              icon: CheckCircle2,
              title: "Controlled process",
              text: "Incomplete sections cannot be skipped.",
            },
          ].map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="rounded-2xl bg-white p-6 border border-slate-200 shadow-sm"
            >
              <div className="w-11 h-11 rounded-xl bg-[#026b07]/10 text-[#026b07] flex items-center justify-center mb-4">
                <Icon size={21} />
              </div>

              <h3 className="text-base font-semibold text-slate-950 mb-2">
                {title}
              </h3>

              <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default RegistryPortal;